import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import {
  IAIEscalationTransmissionMessage,
  IAISessionStateTransmissionMessage,
  IAIStatusTransmissionMessage,
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { selectStage } from '../../../reducers/restaurantSlice';
import { setSessionStart } from '../sessionBoundary/sessionBoundary.slice';
import logger from '../../../utils/logger';
import {
  AIStatusColors,
  DEFAULT_STATUS_MESSAGE,
  SLICE_NAME,
} from './ai.constants';
import { IAIState } from './ai.props';
import { escalationRequiresTakeover, initialState } from './ai.utils';

// Reset the AI "control" state — the fields that drive the status band and the
// escalation/takeover flow — to their idle defaults. Used on a new order/menu
// load and when an order is finalized (the band must not strand the finished
// order's last status). Deliberately does NOT touch the ai_session_state signals
// (turn/sentiment/reused/express): those track the live conversation and clear
// on session turnover, not on finalize — see resetSessionSignals.
const resetControlState = (state: IAIState) => {
  state.isAIAutoMode = true;
  state.isAIEscalation = false;
  state.isAIEscalatedOnCarEnter = false;
  state.requiresTakeover = false;
  state.aiStatus = {
    status: AIStatusColors.green,
    message: DEFAULT_STATUS_MESSAGE,
  };
  state.aiEscalationReason = '';
};

// Clear the HITL 3.0 ai_session_state signals surfaced in the Pulse card. Reset
// on session start (car_enter / setSessionStart) so a new customer starts clean —
// NOT on order finalize: finishing/cancelling an order while the car is still
// present must keep them (the conversation is ongoing), mirroring Menu Board Time.
const resetSessionSignals = (state: IAIState) => {
  state.turn = 0;
  state.sentimentLabel = '';
  state.reusedAnswers = null;
  state.expressModeActive = false;
};

export const aiSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetAI: () => {
      return initialState;
    },
    setIsAIAutoMode: (state, { payload }) => {
      state.isAIAutoMode = payload;
    },
    setIsAIEscalation: (state, { payload }) => {
      state.isAIEscalation = payload;
    },
    setRequiresTakeover: (state, { payload }) => {
      state.requiresTakeover = payload;
    },
    setIsAIEscalatedOnCarEnter: (state, { payload }) => {
      state.isAIEscalatedOnCarEnter = payload;
    },
    setAIStatus: (state, { payload }) => {
      state.aiStatus = payload;
    },
    // Reset ONLY the status-band / control fields (not the ai_session_state
    // signals). Dispatched when an order is finalized so the band drops the
    // finished order's status while Turn/Sentiment survive a finish-while-car-
    // present (they clear on the next session start instead).
    resetAIControlState: (state) => {
      resetControlState(state);
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const { messages, taskRouter, sessionBoundarySessionId } =
          action.payload;
        messages.forEach((message) => {
          if (
            (taskRouter?.currentTaskId &&
              taskRouter?.taskAssignedSessionId !== message.session_id) ||
            (sessionBoundarySessionId &&
              sessionBoundarySessionId !== message.session_id)
          ) {
            //If the current task is from task-router and if the message session is not matching with
            //task-router current task session or the message session is not matching with the current session id in sessionBoundary , don't process the received message
            return;
          }

          if (message.event === EventTypes.carEnter) {
            // A new customer arrived → clear the previous conversation's
            // ai_session_state signals so they don't linger into the new session
            // (the backend then repopulates them from fresh ai_session_state).
            resetSessionSignals(state);
          } else if (message.event === EventTypes.aiEscalation) {
            logger.log('ai_escalation received');
            state.isAIEscalation = true;
            const escalationData = (message as IAIEscalationTransmissionMessage)
              .data;
            state.aiEscalationReason = escalationData.escalation_reason || '';
            // Sticky: once a takeover is requested it stays until the session
            // resets (see the selectStage/selectMenuVersion matcher). A true
            // takeover means the AI halted, so a later recoverable escalation —
            // or a buffered/relayed event that lost the flag — must not silently
            // downgrade the "must take over" alert.
            state.requiresTakeover =
              state.requiresTakeover ||
              escalationRequiresTakeover(escalationData);
            if (message.metadata?.escalate_on_car_enter) {
              state.isAIEscalatedOnCarEnter = true;
            }
          } else if (
            message.event === EventTypes.aiStatus &&
            !(state.aiStatus?.status === AIStatusColors.red) &&
            !state.isAIEscalation
          ) {
            state.aiStatus =
              (message as IAIStatusTransmissionMessage).data || {};
          } else if (message.event === EventTypes.aiSessionState) {
            // HITL 3.0: consolidated per-turn session state → Pulse card fields.
            // Extracted unconditionally; the Pulse card (the only consumer) is
            // gated on hitl_3_0, so these never surface unless the flag is on.
            const sessionState =
              (message as IAISessionStateTransmissionMessage).data || {};
            // Turn count — a plain counter shown default 0 (like Menu Board Time).
            state.turn =
              typeof sessionState.turn === 'number' ? sessionState.turn : 0;
            // Sentiment label — '' when absent so the chip hides.
            state.sentimentLabel = sessionState.sentiment?.label || '';
            // Reused answers — kept raw; formatted for the chip in the UI, null
            // when absent so the chip hides.
            state.reusedAnswers = sessionState.reused_answers ?? null;
            // Whether the AI reports express mode active ("AI - Express mode"
            // chip). Distinct from the selector-driven Express chip.
            state.expressModeActive =
              sessionState.express_mode?.active === true;
          }
        });
      }
    );
    builder.addCase(messagingActions.sendAgentInterception, (state) => {
      if (state.isAIAutoMode) state.isAIAutoMode = false;
    });
    builder.addCase(setSessionStart, (state) => {
      // Task-router session start → a fresh conversation, so clear any stale
      // ai_session_state signals (car_enter covers the single-restaurant flow).
      resetSessionSignals(state);
    });
    builder.addMatcher(
      isAnyOf(selectStage.fulfilled, selectMenuVersion.fulfilled),
      (state) => {
        // New order / menu load: reset the control state only. The session
        // signals are intentionally NOT reset here — a mid-conversation menu
        // reload must not wipe them; they reset on session start instead.
        resetControlState(state);
      }
    );
  },
});

const {
  resetAI,
  resetAIControlState,
  setIsAIAutoMode,
  setIsAIEscalation,
  setIsAIEscalatedOnCarEnter,
  setRequiresTakeover,
  setAIStatus,
} = aiSlice.actions;

export {
  resetAI,
  resetAIControlState,
  setAIStatus,
  setIsAIAutoMode,
  setIsAIEscalation,
  setIsAIEscalatedOnCarEnter,
  setRequiresTakeover,
};
export default aiSlice.reducer;
